import { useMemo, useState } from "react";

type Row = Record<string, unknown>;

export type CopyFieldKey =
  | "title"
  | "date"
  | "performers"
  | "kind"
  | "prefecture"
  | "reception"
  | "officialUrl"
  | "detailUrl"
  | "reserveUrl"
  | "timeRange"
  | "time1"
  | "time2"
  | "time3"
  | "performanceText"
  | "detail"
  | "venue"
  | "fanPost"
  | "memo";

const FIELD_DEFS: { key: CopyFieldKey; label: string }[] = [
  { key: "title", label: "タイトル" },
  { key: "date", label: "開催日" },
  { key: "performers", label: "出演者" },
  { key: "kind", label: "種別" },
  { key: "prefecture", label: "都道府県" },
  { key: "reception", label: "受付期間" },
  { key: "officialUrl", label: "公式URL" },
  { key: "detailUrl", label: "詳細URL" },
  { key: "reserveUrl", label: "予約URL" },
  { key: "timeRange", label: "開始・終了" },
  { key: "time1", label: "公演時間①" },
  { key: "time2", label: "公演時間②" },
  { key: "time3", label: "公演時間③" },
  { key: "performanceText", label: "本番・メモ" },
  { key: "detail", label: "詳細" },
  { key: "venue", label: "会場" },
  { key: "fanPost", label: "ファン投稿情報" },
  { key: "memo", label: "メモ" },
];

function asStr(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

function fieldLine(key: CopyFieldKey, e: Row): string | null {
  switch (key) {
    case "title":
      return asStr(e.event_name) || null;
    case "date":
      return asStr(e.event_date) ? `開催日: ${asStr(e.event_date)}` : null;
    case "performers": {
      try {
        const parsed = JSON.parse(asStr(e.performers_json) || "[]") as unknown;
        const names = Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
        return names.length ? `出演者: ${names.join(" / ")}` : null;
      } catch {
        return null;
      }
    }
    case "kind":
      return asStr(e.event_kind) ? `種別: ${asStr(e.event_kind)}` : null;
    case "prefecture":
      return asStr(e.prefecture) ? `都道府県: ${asStr(e.prefecture)}` : null;
    case "reception": {
      const m = asStr(e.reception_period_text);
      if (m) return `受付期間: ${m}`;
      const rs = asStr(e.reception_start);
      const re = asStr(e.reception_end);
      if (rs || re) return `受付期間: ${rs || "—"} 〜 ${re || "—"}`;
      return null;
    }
    case "officialUrl":
    case "detailUrl": {
      const u = asStr(e.detail_url);
      return u ? `詳細URL: ${u}` : null;
    }
    case "reserveUrl": {
      const u = asStr(e.reserve_url);
      return u ? `予約URL: ${u}` : null;
    }
    case "timeRange": {
      const start = asStr(e.time_start);
      const end = asStr(e.time_end);
      if (start || end) return `開始・終了: ${start || "—"} 〜 ${end || "—"}`;
      return null;
    }
    case "time1": {
      const door = asStr(e.door_time);
      const cur = asStr(e.curtain_time);
      if (door || cur) return `公演時間①: ${[door && `開場 ${door}`, cur && `開演 ${cur}`].filter(Boolean).join(" / ")}`;
      return asStr(e.time_start) ? `公演時間①: ${asStr(e.time_start)}` : null;
    }
    case "time2": {
      const d2 = asStr(e.door_time_2);
      const c2 = asStr(e.curtain_time_2);
      if (d2 || c2) return `公演時間②: ${[d2 && `開場 ${d2}`, c2 && `開演 ${c2}`].filter(Boolean).join(" / ")}`;
      return null;
    }
    case "time3": {
      const d3 = asStr(e.door_time_3);
      const c3 = asStr(e.curtain_time_3);
      if (d3 || c3) return `公演時間③: ${[d3 && `開場 ${d3}`, c3 && `開演 ${c3}`].filter(Boolean).join(" / ")}`;
      return null;
    }
    case "detail":
      return asStr(e.description) ? `詳細: ${asStr(e.description)}` : null;
    case "performanceText":
      return asStr(e.performance_text) ? `本番・メモ: ${asStr(e.performance_text)}` : null;
    case "venue":
      return [asStr(e.prefecture), asStr(e.venue)].filter(Boolean).length
        ? `会場: ${[asStr(e.prefecture), asStr(e.venue)].filter(Boolean).join(" ")}`
        : null;
    case "fanPost": {
      const lines = [
        asStr(e.fan_group_name) ? `グループ名: ${asStr(e.fan_group_name)}` : "",
        asStr(e.fan_artist_name) ? `アーティスト名: ${asStr(e.fan_artist_name)}` : "",
        asStr(e.performance_count) ? `公演数: ${asStr(e.performance_count)}` : "",
        asStr(e.source_title) ? `取得タイトル: ${asStr(e.source_title)}` : "",
      ].filter(Boolean);
      return lines.length ? lines.join("\n") : null;
    }
    case "memo":
      return asStr(e.memo) ? `メモ: ${asStr(e.memo)}` : null;
    default:
      return null;
  }
}

export function buildAdminCopyText(e: Row, picks: Record<CopyFieldKey, boolean>): string {
  return FIELD_DEFS.filter((f) => picks[f.key])
    .map((f) => fieldLine(f.key, e))
    .filter(Boolean)
    .join("\n");
}

export function buildAdminXText(e: Row, picks: Record<CopyFieldKey, boolean>): string {
  const body = buildAdminCopyText(e, picks);
  return body ? `${body}\n\n#佳林ちゃんに会いに行ってみよう` : "";
}

export function openXIntent(text: string): void {
  const enc = encodeURIComponent(text);
  window.open(`twitter://post?message=${enc}`, "_blank");
  setTimeout(() => window.open(`https://x.com/intent/tweet?text=${enc}`, "_blank"), 380);
}

export function AdminCopyXFields({ e }: { e: Row }) {
  const previews = useMemo(() => {
    const m: Record<CopyFieldKey, string | null> = {} as Record<CopyFieldKey, string | null>;
    for (const f of FIELD_DEFS) m[f.key] = fieldLine(f.key, e);
    return m;
  }, [e]);

  const [pick, setPick] = useState<Record<CopyFieldKey, boolean>>({
    title: true,
    date: true,
    performers: true,
    kind: false,
    prefecture: true,
    reception: true,
    officialUrl: true,
    detailUrl: false,
    reserveUrl: false,
    timeRange: true,
    time1: true,
    time2: false,
    time3: false,
    performanceText: true,
    detail: false,
    venue: true,
    fanPost: true,
    memo: false,
  });

  const allKeys = FIELD_DEFS.map((f) => f.key);
  const enabledKeys = allKeys.filter((k) => previews[k]);

  const selectAll = () => {
    const next = { ...pick };
    for (const k of allKeys) next[k] = Boolean(previews[k]);
    setPick(next);
  };

  const clearAll = () => {
    const next = { ...pick };
    for (const k of allKeys) next[k] = false;
    setPick(next);
  };

  const copy = async () => {
    const text = buildAdminCopyText(e, pick);
    await navigator.clipboard.writeText(text);
  };

  return (
    <div className="na-copy-block">
      <div className="na-copy-guide">
        下の項目にチェックを入れると、その情報だけをまとめてコピー／X投稿できます。
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <button type="button" className="btn small secondary" onClick={() => void copy()}>
          チェックした情報をコピペ
        </button>
        <button type="button" className="btn small secondary" onClick={() => openXIntent(buildAdminXText(e, pick))}>
          チェックした情報をXに投稿
        </button>
        <button type="button" className="btn small secondary" onClick={selectAll}>
          全て選択
        </button>
        <button type="button" className="btn small secondary" onClick={clearAll}>
          クリア
        </button>
      </div>
      {FIELD_DEFS.map((f) => {
        const line = previews[f.key];
        const disabled = !line;
        return (
          <div key={f.key} className="na-copy-field-row">
            <label className={disabled ? "na-copy-field--disabled" : undefined}>
              <input
                type="checkbox"
                disabled={disabled}
                checked={!disabled && Boolean(pick[f.key])}
                onChange={(ev) => setPick({ ...pick, [f.key]: ev.target.checked })}
              />{" "}
              {f.label}
            </label>
            <div className={`na-copy-preview${disabled ? " na-copy-preview--empty" : ""}`}>{line ?? "（データなし）"}</div>
          </div>
        );
      })}
    </div>
  );
}
